import React, { useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { GeoAltFill } from "react-bootstrap-icons";
import { v4 as uuidv4 } from "uuid";

import {
  createPlacesHttpService,
  getAddressPredictions,
  getPlaceDetails,
  PlacePrediction,
  toLegacyGooglePlace,
} from "../../../services/placesService";

interface AddressAutocompleteProps {
  id: string;
  name: string;
  className?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
  placeholder?: string;
  value: string;
  countryCode: string;
  readOnly?: boolean;
  disabled?: boolean;
  isInvalid?: boolean;
  ariaRequired?: boolean;
  ariaDescribedBy?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  onPlaceSelected: (place: any) => void;
}

const getPredictionText = (prediction: PlacePrediction) => {
  const structuredMain = prediction.structuredFormat?.mainText?.text?.trim();
  const structuredSecondary = prediction.structuredFormat?.secondaryText?.text?.trim();
  if (structuredMain) {
    return {
      primary: structuredMain,
      secondary: structuredSecondary ?? "",
    };
  }

  const [primary = "", ...secondaryParts] = (prediction.text?.text ?? "").split(",");
  return {
    primary: primary.trim(),
    secondary: secondaryParts.join(",").trim(),
  };
};

const renderMatchedText = (text: string, input: string) => {
  const query = input.trim();
  if (!query) {
    return text;
  }

  const matchIndex = text.toLocaleLowerCase().indexOf(query.toLocaleLowerCase());
  if (matchIndex < 0) {
    return text;
  }

  return (
    <>
      {text.slice(0, matchIndex)}
      <strong>{text.slice(matchIndex, matchIndex + query.length)}</strong>
      {text.slice(matchIndex + query.length)}
    </>
  );
};

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  id,
  name,
  className,
  inputRef,
  placeholder,
  value,
  countryCode,
  readOnly,
  disabled,
  isInvalid,
  ariaRequired,
  ariaDescribedBy,
  onChange,
  onBlur,
  onPlaceSelected,
}) => {
  const httpService = useMemo(() => createPlacesHttpService(), []);
  const sessionTokenRef = useRef(uuidv4());
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const requestSequenceRef = useRef(0);
  const [suggestions, setSuggestions] = useState<PlacePrediction[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const listboxId = `${id}-suggestions`;

  useEffect(() => () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
  }, []);

  const selectPrediction = async (prediction: PlacePrediction) => {
    requestSequenceRef.current += 1;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setSuggestions([]);
    setActiveIndex(-1);

    try {
      const details = await getPlaceDetails(
        httpService,
        prediction.placeId,
        sessionTokenRef.current
      );
      onPlaceSelected(toLegacyGooglePlace(details));
      sessionTokenRef.current = uuidv4();
    } catch (error) {
      console.error("Unable to load Google place details", error);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event);
    const input = event.currentTarget.value.trim();

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (input.length < 3) {
      requestSequenceRef.current += 1;
      setSuggestions([]);
      setActiveIndex(-1);
      return;
    }

    const requestSequence = ++requestSequenceRef.current;
    debounceRef.current = setTimeout(async () => {
      try {
        const predictions = await getAddressPredictions(
          httpService,
          input,
          countryCode,
          sessionTokenRef.current
        );
        if (requestSequence === requestSequenceRef.current) {
          setSuggestions(predictions);
          setActiveIndex(-1);
        }
      } catch (error) {
        if (requestSequence === requestSequenceRef.current) {
          setSuggestions([]);
          setActiveIndex(-1);
        }
        console.error("Unable to load Google address predictions", error);
      }
    }, 300);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    requestSequenceRef.current += 1;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setSuggestions([]);
    setActiveIndex(-1);
    onBlur(event);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!suggestions.length) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) => Math.min(current + 1, suggestions.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) => Math.max(current - 1, 0));
    } else if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      void selectPrediction(suggestions[activeIndex]);
    } else if (event.key === "Escape") {
      setSuggestions([]);
      setActiveIndex(-1);
    }
  };

  return (
    <div className={`address-autocomplete${isInvalid ? " is-invalid" : ""}`}>
      <Form.Control
        id={id}
        name={name}
        className={className}
        ref={inputRef}
        placeholder={placeholder}
        type="text"
        value={value}
        autoComplete="new-password"
        readOnly={readOnly}
        disabled={disabled}
        isInvalid={isInvalid}
        aria-required={ariaRequired}
        aria-invalid={isInvalid}
        aria-describedby={ariaDescribedBy}
        aria-autocomplete="list"
        aria-controls={suggestions.length ? listboxId : undefined}
        aria-expanded={suggestions.length > 0}
        aria-activedescendant={
          activeIndex >= 0 ? `${listboxId}-${activeIndex}` : undefined
        }
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
      {suggestions.length > 0 && (
        <div className="address-autocomplete__menu">
          <div
            id={listboxId}
            className="address-autocomplete__suggestions"
            role="listbox"
          >
            {suggestions.map((prediction, index) => {
              const { primary, secondary } = getPredictionText(prediction);
              return (
                <button
                  id={`${listboxId}-${index}`}
                  key={prediction.placeId}
                  className={`address-autocomplete__suggestion${
                    activeIndex === index ? " active" : ""
                  }`}
                  type="button"
                  role="option"
                  aria-selected={activeIndex === index}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => void selectPrediction(prediction)}
                >
                  <GeoAltFill
                    aria-hidden="true"
                    className="address-autocomplete__pin"
                  />
                  <span className="address-autocomplete__text">
                    <span className="address-autocomplete__primary">
                      {renderMatchedText(primary, value)}
                    </span>
                    {secondary && (
                      <span className="address-autocomplete__secondary">
                        {secondary}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="address-autocomplete__attribution">
            powered by <span translate="no">Google Maps</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
